import React from 'react';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  RiseOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { IPipelineRun } from '../../../core/modules/pipelines/pipelines.model';
import Sparkline from './Sparkline';
import { formatDuration, formatRelative } from '../utils/formatStage';
import { BRENDA_GREEN } from '../utils/statusColors';

const Strip = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const Stat = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 92px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const IconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
`;

const PrimaryValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1.1;
`;

const Subtle = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface IStatStripProps {
  current: IPipelineRun | null;
  runs: IPipelineRun[];
  showStages?: boolean;
  showDuration?: boolean;
  showTrend?: boolean;
}

const StatStrip: React.FC<IStatStripProps> = ({
  current,
  runs,
  showStages = true,
  showDuration = true,
  showTrend = true
}) => {
  const stages = current?.stages ?? [];
  const completed = stages.filter((s) => s.status === 'success').length;
  const running = stages.filter((s) => s.status === 'running').length;

  const validDurations = runs
    .map((r) => r.durationSec)
    .filter((d): d is number => typeof d === 'number');
  const avgDuration = validDurations.length > 0
    ? Math.round(validDurations.reduce((a, b) => a + b, 0) / validDurations.length)
    : null;

  // Failure count is bound to the runs currently in view (matches the rows
  // rendered in the table below) so users don't see a different number than
  // what they can count themselves.
  const failuresVisible = runs.filter((r) => r.status === 'failed').length;

  const trendValues = runs
    .slice()
    .reverse()
    .map((r) => r.durationSec ?? null);

  const cols = [true, showDuration, showStages, showTrend].filter(Boolean).length;

  return (
    <Strip $cols={cols}>
      <Stat>
        <Header>
          <IconBox><CalendarOutlined /></IconBox>
          Last run
        </Header>
        <PrimaryValue>{current ? formatRelative(current.startedAt) : '—'}</PrimaryValue>
        <Subtle>
          {current?.startedAt
            ? new Date(current.startedAt).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
            })
            : '—'}
        </Subtle>
      </Stat>

      {showDuration && (
        <Stat>
          <Header>
            <IconBox><ClockCircleOutlined /></IconBox>
            Duration
          </Header>
          <PrimaryValue>{formatDuration(current?.durationSec)}</PrimaryValue>
          <Subtle>Avg: {formatDuration(avgDuration)}</Subtle>
        </Stat>
      )}

      {showStages && (
        <Stat>
          <Header>
            <IconBox><DatabaseOutlined /></IconBox>
            Stages
          </Header>
          <PrimaryValue>{completed}/{stages.length || '—'}</PrimaryValue>
          <Subtle>
            {stages.length === 0
              ? '—'
              : running > 0
                ? <span style={{ color: '#1890ff' }}>{running} running</span>
                : <span style={{ color: BRENDA_GREEN }}>complete</span>}
          </Subtle>
        </Stat>
      )}

      {showTrend && (
        <Stat>
          <Header>
            <IconBox><RiseOutlined /></IconBox>
            Trend
          </Header>
          <PrimaryValue style={{ display: 'block', width: '100%' }}>
            <Sparkline values={trendValues} height={28} />
          </PrimaryValue>
          <Subtle style={{ color: failuresVisible > 0 ? '#cf1322' : 'rgba(0,0,0,0.45)' }}>
            {failuresVisible} {failuresVisible === 1 ? 'failure' : 'failures'}
          </Subtle>
        </Stat>
      )}

    </Strip>
  );
};

export default StatStrip;
